import { computed, Directive, input, signal } from '@angular/core';
import { hlm } from '@spartan-ng/brain/core';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ClassValue } from 'clsx';
import { injectBrnButtonConfig } from './hlm-button.token';

export const buttonVariants = cva(
  'inline-flex items-center text-nowrap justify-center rounded-md typography-label-default transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background hover:!no-underline',
  {
    variants: {
      variant: {
        default: 'bg-brand text-brand hover:bg-brand-hover hover:text-brand-hover',
        soft: 'bg-subtle text-default hover:bg-subtle-hover hover:text-default-hover [&.selected]:bg-subtle-active [&.selected]:text-default-active',
        outline: 'border border-strong text-default hover:bg-subtle',
        ghost: 'hover:bg-accent hover:text-accent-foreground [&.selected]:bg-subtle',
        link: 'text-utility-info hover:!underline !underline-offset-2',
        tab: 'bg-transparent text-default data-[state=active]:bg-default data-[state=active]:text-default',
        destructive: 'bg-utility-error text-utility-error hover:bg-utility-error-hover hover:text-utility-error-hover',
      },
      size: {
        default: 'py-md px-lg',
        xs: 'h-8 px-2 rounded-sm',
        sm: 'h-9 px-3 rounded-md',
        lg: 'h-11 px-8 rounded-md',
        icon: 'h-10 w-10',
        'icon-sm': 'h-8 w-8 rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);
export type ButtonVariants = VariantProps<typeof buttonVariants>;

@Directive({
  selector: '[hlmBtn]',
  standalone: true,
  exportAs: 'hlmBtn',
  host: {
    '[class]': '_computedClass()',
  },
})
export class HlmButtonDirective {
  private readonly _config = injectBrnButtonConfig();

  private readonly _additionalClasses = signal<ClassValue>('');

  public readonly userClass = input<ClassValue>('', { alias: 'class' });

  protected readonly _computedClass = computed(() =>
    hlm(buttonVariants({ variant: this.variant(), size: this.size() }), this.userClass(), this._additionalClasses()),
  );

  public readonly variant = input<ButtonVariants['variant']>(this._config.variant);

  public readonly size = input<ButtonVariants['size']>(this._config.size);

  setClass(classes: string): void {
    this._additionalClasses.set(classes);
  }
}
